import { useState, useEffect, useCallback, useRef } from 'react';

interface UseDirtyFormOptions {
  chatbotId: string;
  formName: string;
  savedValues: Record<string, string>;
  currentValues: Record<string, string>;
  enabled: boolean;
}

interface UseDirtyFormReturn {
  isDirty: boolean;
  draft: Record<string, string> | null;
  clearDraft: () => void;
}

function storageKey(chatbotId: string, formName: string) {
  return `ni-draft-${chatbotId}-${formName}`;
}

export function useDirtyForm({
  chatbotId,
  formName,
  savedValues,
  currentValues,
  enabled,
}: UseDirtyFormOptions): UseDirtyFormReturn {
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const currentValuesRef = useRef(currentValues);
  const isDirtyRef = useRef(false);
  const lastWrittenRef = useRef('');
  currentValuesRef.current = currentValues;

  const isDirty = enabled && Object.keys(savedValues).some(
    (key) => (currentValues[key] ?? '') !== (savedValues[key] ?? '')
  );
  isDirtyRef.current = isDirty;

  useEffect(() => {
    const key = storageKey(chatbotId, formName);
    try {
      const stored = localStorage.getItem(key);
      if (stored) setDraft(JSON.parse(stored));
    } catch {
      localStorage.removeItem(key);
    }
  }, [chatbotId, formName]);

  useEffect(() => {
    if (!enabled) return;

    function saveDraft() {
      if (!isDirtyRef.current) return;
      const key = storageKey(chatbotId, formName);
      const json = JSON.stringify(currentValuesRef.current);
      if (json === lastWrittenRef.current) return;
      try {
        localStorage.setItem(key, json);
        lastWrittenRef.current = json;
      } catch {}
    }

    const interval = setInterval(saveDraft, 30_000);

    // Flush draft on page close and on unmount
    const handleBeforeUnload = () => saveDraft();
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      saveDraft();
    };
  }, [chatbotId, formName, enabled]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(storageKey(chatbotId, formName));
    lastWrittenRef.current = '';
    setDraft(null);
  }, [chatbotId, formName]);

  return { isDirty, draft, clearDraft };
}
